import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, ShieldCheck, Loader2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { getApiKeys, requestKeyOtp, saveApiKeys, formatApiErrorDetail } from "@/lib/auth_api";
import { toast } from "sonner";

/**
 * OTP-gated API-key manager.
 *
 * Props:
 *   bot        "main" | "smartgrid"
 *   isRunning  boolean — edits are blocked (and warned) while the bot runs
 */
export const ApiKeysSection = ({ bot = "main", isRunning = false }) => {
    const [data, setData] = useState(null);       // masked view from server
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [network, setNetwork] = useState("mainnet");
    const [signer, setSigner] = useState("");
    const [pk, setPk] = useState("");
    const [userAddr, setUserAddr] = useState("");
    const [code, setCode] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [sending, setSending] = useState(false);
    const [saving, setSaving] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try { setData(await getApiKeys(bot)); }
        catch (e) { /* surfaced on demand */ }
        finally { setLoading(false); }
    }, [bot]);

    useEffect(() => { refresh(); }, [refresh]);

    const startEdit = () => {
        setEditing(true);
        setSigner(""); setPk(""); setUserAddr(""); setCode(""); setOtpSent(false);
    };
    const cancelEdit = () => {
        setEditing(false);
        setSigner(""); setPk(""); setUserAddr(""); setCode(""); setOtpSent(false);
    };

    const sendOtp = async () => {
        setSending(true);
        try {
            const r = await requestKeyOtp(bot);
            setOtpSent(true);
            if (r?.emailed === false) {
                toast.warning("Email unavailable — code written to the server log",
                    { description: r?.note || "Read it via journalctl / docker logs" });
            } else {
                toast.success("Verification code emailed", { description: r?.to ? `Sent to ${r.to}` : undefined });
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to send code");
        } finally { setSending(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            await saveApiKeys(bot, {
                code: code.trim(),
                network,
                signer_address: signer.trim(),
                private_key: pk.trim(),
                user_address: userAddr.trim(),
            });
            toast.success(`API keys saved for ${network.toUpperCase()}`);
            cancelEdit();
            await refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
        } finally { setSaving(false); }
    };

    const nets = data?.networks || {};
    const canSave = code.length === 6 && signer.trim() && pk.trim() && !saving;

    return (
        <div
            className="rounded-md p-3 mt-2"
            style={{ background: "rgba(61,167,255,0.05)", border: "1px solid rgba(61,167,255,0.25)" }}
            data-testid={`apikeys-panel-${bot}`}
        >
            <div className="flex items-center gap-2 mb-1">
                <KeyRound size={14} color="#3DA7FF" />
                <div className="text-[12px] font-semibold text-[#E6EDF6]">API Key Management</div>
            </div>
            <div className="text-[10px] text-[#A7B4C6] mb-3">
                AsterDex signing credentials, encrypted at rest. Editing requires an emailed
                verification code and the bot must be stopped.
            </div>

            {/* Masked current state */}
            {!editing && (
                <div className="space-y-2">
                    {["mainnet", "testnet"].map((net) => {
                        const n = nets[net] || {};
                        return (
                            <div key={net} className="flex items-center justify-between gap-2 text-[11px] font-mono"
                                data-testid={`apikeys-row-${bot}-${net}`}>
                                <span className="uppercase tracking-[0.12em] text-[#6F7D92]">{net}</span>
                                {n.configured ? (
                                    <span className="flex items-center gap-2 text-[#A7B4C6]">
                                        <ShieldCheck size={12} color="#2BE38B" />
                                        signer {n.signer_masked} · key {n.private_key_masked}
                                    </span>
                                ) : (
                                    <span className="text-[#6F7D92]">not configured</span>
                                )}
                            </div>
                        );
                    })}
                    <div className="pt-1">
                        <Button
                            variant="outline"
                            onClick={startEdit}
                            disabled={isRunning}
                            className="btn-anim border-[#1B2A3A] bg-transparent text-[#A7B4C6] hover:bg-[#121C28] hover:text-[#E6EDF6] gap-1.5 h-8 text-[12px]"
                            data-testid={`apikeys-edit-${bot}`}
                        >
                            <Pencil size={13} /> Edit keys
                        </Button>
                        {isRunning && (
                            <span className="ml-2 text-[10px] text-[#FFB020]">Stop the bot to edit keys.</span>
                        )}
                    </div>
                </div>
            )}

            {/* Edit form */}
            {editing && (
                <div className="space-y-3">
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Network</Label>
                        <select
                            value={network}
                            onChange={(e) => setNetwork(e.target.value)}
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#3DA7FF]"
                            data-testid={`apikeys-network-${bot}`}
                        >
                            <option value="mainnet">MAINNET</option>
                            <option value="testnet">TESTNET</option>
                        </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Signer Address</Label>
                        <input
                            value={signer}
                            onChange={(e) => setSigner(e.target.value)}
                            placeholder="0x…"
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#3DA7FF]"
                            data-testid={`apikeys-signer-${bot}`}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Private Key</Label>
                        <input
                            type="password"
                            value={pk}
                            onChange={(e) => setPk(e.target.value)}
                            placeholder="64 hex chars (0x optional)"
                            autoComplete="new-password"
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#3DA7FF]"
                            data-testid={`apikeys-pk-${bot}`}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">User Address (optional)</Label>
                        <input
                            value={userAddr}
                            onChange={(e) => setUserAddr(e.target.value)}
                            placeholder="0x… (leave blank to keep unchanged)"
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#3DA7FF]"
                            data-testid={`apikeys-useraddr-${bot}`}
                        />
                    </div>

                    {/* OTP */}
                    {!otpSent ? (
                        <Button
                            onClick={sendOtp}
                            disabled={sending}
                            className="bg-[#3DA7FF] hover:bg-[#5EB8FF] text-[#0B0F14] gap-1.5 h-9"
                            data-testid={`apikeys-send-otp-${bot}`}
                        >
                            {sending ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                            Email verification code
                        </Button>
                    ) : (
                        <div className="space-y-2">
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">
                                6-digit code
                            </Label>
                            <div data-testid={`apikeys-otp-${bot}`}>
                                <InputOTP maxLength={6} value={code} onChange={setCode}>
                                    <InputOTPGroup>
                                        <InputOTPSlot index={0} />
                                        <InputOTPSlot index={1} />
                                        <InputOTPSlot index={2} />
                                        <InputOTPSlot index={3} />
                                        <InputOTPSlot index={4} />
                                        <InputOTPSlot index={5} />
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>
                            <button
                                type="button"
                                onClick={sendOtp}
                                disabled={sending}
                                className="text-[11px] text-[#2EE6D6] hover:underline disabled:opacity-40"
                                data-testid={`apikeys-resend-${bot}`}
                            >
                                {sending ? "Sending…" : "Resend code"}
                            </button>
                        </div>
                    )}

                    <div className="flex items-center gap-2 pt-1">
                        <Button
                            onClick={save}
                            disabled={!canSave}
                            className="bg-[#2EE6D6] hover:bg-[#43F0E0] text-[#0B0F14] gap-1.5 h-9 disabled:opacity-40"
                            data-testid={`apikeys-save-${bot}`}
                        >
                            {saving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                            Save keys
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={cancelEdit}
                            disabled={saving}
                            data-testid={`apikeys-cancel-${bot}`}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApiKeysSection;
